import { Injectable, inject } from '@angular/core';
import {
  FeatureService,
  GlobalStore,
} from '@maximizer/core/shared/data-access';
import { OAuth2Service } from '@maximizer/oauth2/data-access';
import { InsightsService } from '@maximizer/core/shared/insights';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { catchError, filter, first, interval, timeout } from 'rxjs';
import { Event } from '@maximizer/outlook/shared/domain';
import { OutlookStore } from '../state';
import { OutlookInsightsEvents } from '../models';

@Injectable()
export class IdentityService {
  private readonly globalStore = inject(GlobalStore);
  private readonly oauth2Service = inject(OAuth2Service);
  private readonly insightsService = inject(InsightsService);
  private readonly outlookStore = inject(OutlookStore);
  private readonly feature = inject(FeatureService);

  populateMaximizerIdentity(): void {
    let segments: string[] = [];
    const isCloud = this.oauth2Service.getIsCloudFromStorage();
    const loginDetails = isCloud
      ? this.oauth2Service.getStorageLoginDetails()
      : this.oauth2Service.getStorageOnPremLoginDetails();

    interval(500)
      .pipe(
        filter(() => !!this.globalStore.session() && !!loginDetails),
        first(),
        timeout(8000),
        catchError(() => {
          this.insightsService.trackException({
            error: new Error(
              'Failed to initialize MaximizerIdentity due to timeout',
            ),
          });
          return [];
        }),
      )
      .pipe()
      .subscribe(() => {
        const session = this.globalStore.session();

        let customerId = '';
        let workspaceId = null;

        if (isCloud && loginDetails && 'tenant' in loginDetails) {
          customerId = loginDetails.tenant?.id?.toLowerCase() ?? '';
          workspaceId =
            loginDetails.tenant?.workspaces?.[0]?.id?.toLowerCase() ?? '';
        } else if (!isCloud && loginDetails && 'customerId' in loginDetails) {
          customerId = loginDetails.customerId?.toLowerCase() ?? '';
        }

        window.maximizerIdentity = {
          alias: session?.alias ? session.alias.toLowerCase() : null,
          application: 'outlook',
          customer_id: customerId,
          database_id: (session?.database ?? '').toLowerCase(),
          platform_type: isCloud ? 'cloud' : 'on-prem',
          user_login: (session?.user.id ?? '').toLowerCase(),
          workspace_id: workspaceId,
          database_name: session?.displayName ?? null,
          user_name: session?.user.name ?? null,
          email: session?.user.email1.email
            ? session?.user.email1.email.toLocaleLowerCase()
            : null,
          vertical: this.outlookStore.vertical(),
          language: this.outlookStore.language(),
        };

        if (
          !this.feature.isFeatureOn('personal-data-tracking-enabled', false)
        ) {
          segments.push('no-personal-data-tracking');
        }

        if (segments.length) {
          window.maximizerIdentity.segments = segments.join(',');
        }

        this.readyMaximizerIdentify();
      });
  }

  private readyMaximizerIdentify(): void {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: Event.MaximizerIdentityReady,
      });
    } else {
      this.insightsService.trackTrace(
        {
          message: 'identity-service-datalayer-unavailable',
          severityLevel: SeverityLevel.Verbose,
        },
        {
          component: 'IdentityService',
          eventId: OutlookInsightsEvents.OutlookAuth,
        },
      );
    }
  }
}
